import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { DatePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
    ViewEncapsulation,
} from '@angular/core';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { FuseLoadingService } from '@fuse/services/loading';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Notification } from 'app/layout/common/notifications/notifications.types';
import { GetUserNotificationsService } from 'app/layout/common/notifications/user-notifications/get-user-notifications.service';
import { Subject } from 'rxjs';
import { UserNotification } from './user-notifications/user-notification-types';
import {  markUnreadNotifications } from 'app/layout/common/notifications/user-notifications/user-notifications-utils';
import { AlertDisplayService } from 'app/layout/common/alert/alert-display.service';
import { TranslocoDatePipe } from '@jsverse/transloco-locale';

@Component({
    selector: 'notifications',
    templateUrl: './notifications.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'notifications',
    standalone: true,
    imports: [
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        NgClass,
        NgTemplateOutlet,
        RouterLink,
        DatePipe,
        TranslocoPipe,
        MatProgressSpinnerModule,
        TranslocoDatePipe
    ],
})
export class NotificationsComponent implements OnInit, OnDestroy {
    @ViewChild('notificationsOrigin') private _notificationsOrigin: MatButton;
    @ViewChild('notificationsPanel')
    private _notificationsPanel: TemplateRef<any>;

    notifications: UserNotification[];
    currentPage: number = 1;
    isLoading: boolean = false;
    hasMoreNotifications: boolean = true; 
    unreadCount: number = 0;
    private _overlayRef: OverlayRef;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    private _alertDisplayService = inject(AlertDisplayService);
    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _getUserNotificationsService: GetUserNotificationsService,
        private _overlay: Overlay,
        private _fuseLoadingService: FuseLoadingService,
        private _viewContainerRef: ViewContainerRef,
        private _translocoService: TranslocoService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        this._getUserNotificationsService.execute(true, this.currentPage).subscribe({
            next: (response) => {
                const pageSize = 20;
                this.notifications = markUnreadNotifications(response.queryResult || [], this.currentPage, pageSize);
    
                const lastReadNotificationId = localStorage.getItem('lastReadNotificationId');
    
                if (lastReadNotificationId) {
                    const lastReadId = parseInt(lastReadNotificationId, 10);
                    this.notifications.forEach(notification => {
                        notification['read'] = notification.id <= lastReadId;
                    });
                }

                this._calculateUnreadCount();
                this._changeDetectorRef.markForCheck();
            },
            error: (error) => {
                console.error('Error fetching notifications:', error);
                this._alertDisplayService.showError(this._translocoService.translate('An error occurred while performing the operation. Please try again'));
            },
        });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();

        // Dispose the overlay
        if (this._overlayRef) {
            this._overlayRef.dispose();
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------


    closePanel(): void {
        if (this.notifications.length > 0) {
            const firstVisibleNotification = this.notifications[0];
            localStorage.setItem('lastReadNotificationId', firstVisibleNotification.id.toString());
        }
        this.notifications = [];
        this.currentPage = 1; 

        this._overlayRef.detach();
    }

    loadMoreNotifications(): void {
        if (this.isLoading || !this.hasMoreNotifications) {
            return;
        }
        this.currentPage++
        this._fuseLoadingService.setAutoMode(false);
        this.isLoading = true;
    
        this._getUserNotificationsService.execute(true, this.currentPage).subscribe({
            next: (response) => {
                if (response.queryResult.length > 0) {
                    const newNotifications = response.queryResult;
                    this.notifications.push(...newNotifications);
    
                    const lastReadNotificationId = localStorage.getItem('lastReadNotificationId');
                    const lastReadId = lastReadNotificationId ? parseInt(lastReadNotificationId, 10) : 0;
    
                    newNotifications.forEach(notification => {
                        if (notification.id <= lastReadId) {
                            notification['read'] = true; 
                        }
                    });
    
                    this._calculateUnreadCount();
                } else {
                    this.hasMoreNotifications = false; 
                }
    
                this.isLoading = false;
                this._changeDetectorRef.markForCheck();
            },
            error: (error) => {
                this._alertDisplayService.showError(this._translocoService.translate('An error occurred while performing the operation. Please try again'));
                console.error('Error fetching notifications:', error);
                this.isLoading = false;
            },
        });
        this._fuseLoadingService.setAutoMode(true);
    }

    onScroll(event: any): void {
        const element = event.target;
        if (element.scrollHeight - element.scrollTop <= element.clientHeight + 50) {
            this.loadMoreNotifications();
        }
    }

    /**
     * Open the notifications panel
     */
    openPanel(): void {
        // Return if the notifications panel or its origin is not defined
        if (!this._notificationsPanel || !this._notificationsOrigin) {
            return;
        }

        // Create the overlay if it doesn't exist
        if (!this._overlayRef) {
            this._createOverlay();
        }

        // Attach the portal to the overlay
        this._overlayRef.attach(
            new TemplatePortal(this._notificationsPanel, this._viewContainerRef)
        );
    }


    /**
     * Mark all notifications as read
     */
    markAllAsRead(): void {
        // Mark all as read
        // this._notificationsService.markAllAsRead().subscribe();
    }

    /**
     * Toggle read status of the given notification
     */
    toggleRead(notification: UserNotification): void {
        if (!notification['read']) {
            notification['read'] = true;
    
            const storedId = localStorage.getItem('lastReadNotificationId');
            const currentId = storedId ? parseInt(storedId, 10) : 0;
    
            if (notification.id > currentId) {
                localStorage.setItem('lastReadNotificationId', notification.id.toString());
            }
    
            this.unreadCount--;
            this._changeDetectorRef.markForCheck();
        }
    }

    /**
     * Delete the given notification
     */
    delete(notification: Notification): void {
        // Delete the notification
        // this._notificationsService.delete(notification.id).subscribe();
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Create the overlay
     */
    private _createOverlay(): void {
        // Create the overlay
        this._overlayRef = this._overlay.create({
            hasBackdrop: true,
            backdropClass: 'fuse-backdrop-on-mobile',
            scrollStrategy: this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay
                .position()
                .flexibleConnectedTo(
                    this._notificationsOrigin._elementRef.nativeElement
                )
                .withLockedPosition(true)
                .withPush(true)
                .withPositions([
                    {
                        originX: 'start',
                        originY: 'bottom',
                        overlayX: 'start',
                        overlayY: 'top',
                    },
                    {
                        originX: 'start',
                        originY: 'top',
                        overlayX: 'start',
                        overlayY: 'bottom',
                    },
                    {
                        originX: 'end',
                        originY: 'bottom',
                        overlayX: 'end',
                        overlayY: 'top',
                    },
                    {
                        originX: 'end',
                        originY: 'top',
                        overlayX: 'end',
                        overlayY: 'bottom',
                    },
                ]),
        });

        // Detach the overlay from the portal on backdrop click
        this._overlayRef.backdropClick().subscribe(() => {
            this._overlayRef.detach();
        });
    }

    /**
     * Calculate the unread count
     *
     * @private
     */
    private _calculateUnreadCount(): void {
        const lastReadNotificationId = localStorage.getItem('lastReadNotificationId');
        const lastReadId = lastReadNotificationId ? parseInt(lastReadNotificationId, 10) : 0;
        this.unreadCount = this.notifications.filter(notification => notification.id > lastReadId).length;
    }
}
